/**
 * comet_view — comets and the other wanderers (natural objects, 2026-10-07; catalog SB-08 … SB-11, SB-13, SB-14).
 *
 * A COMET is a dark kilometre of rock and ice (albedo ~0.04 — "blacker than coal"), solid, on a Keplerian path on the
 * WALL clock (natural_orbit.orbitAt — the nav list computes the same place). Starlight wakes it: its ACTIVITY grows with
 * the logarithm of the light it stands in (world_look.cometActivity), and with it a coma (a 1/ρ glow), jets on its lit
 * side, and TWO TAILS —
 *   the ION tail: narrow, straight, bluish (CO⁺), EXACTLY away from the star (the stellar wind combs it);
 *   the DUST tail: broad, pale, a fan that starts away from the star and curls BACK ALONG THE ORBIT (each grain falls
 *                  behind on its own path) — world_look.cometFrame / dustTailPoint are the rule and its mirror.
 * Neither tail ever points "behind the motion": a comet leaving its star flies TAIL FIRST. A main-belt comet is faint, a
 * dormant one is a dark rock with no tail at all. In the Commons Reach the sun is a direction, not a place: its comets are
 * fully awake under that one direction (`env.sunAt` returns Infinity).
 *
 * Also here, because they share the comet's machinery: a CENTAUR (a dark body among the giants, sometimes in a faint
 * coma of its own), an INTERSTELLAR VISITOR (elongated, reddish, tumbling, inert — it crosses once and is gone) and a
 * METEOROID STREAM (the pebbles a comet shed along its own orbit: dark fragments with no trail, seen when you are among them).
 */
import * as THREE from 'three/webgpu';
import type { NaturalEnv, ColliderGroup } from './natural_api.js';
import type { SystemMakeup, FeatureMakeup, OrbitMakeup } from './natural_types.js';
import { orbitAt, orbitPoint, meanAnomalyAt, type V3 } from './natural_orbit.js';
import { STAR_HEAT } from './system_makeup.js';
import {
  cometActivity, cometSizes, cometFrame, cometFrameFixed, orbitVelocity, visitorAt, streamPebbles, mix01,
  COMET_LOOK, centaurLook, VISITOR_LOOK, type CometFrame,
} from './world_look.js';
import { buildTail, buildGlow, anyPerp, type TailHandle, type GlowHandle } from './world_layers.js';
import type { RockKit } from './smallbody_view.js';
import { deferDisposeTree } from '../../defer_dispose.js';

export interface CometSet {
  /** How many comets it draws (the Commons Reach's sprite comets stand down when > 0). */
  count: number;
  frame(nowS: number): void;
  state(): Record<string, unknown>;
  dispose(): void;
}
type CometF = Extract<FeatureMakeup, { kind: 'comet' }>;
interface Comet {
  f: CometF; group: THREE.Group; abs: THREE.Vector3; nucleus: THREE.Group; col: ColliderGroup;
  glow: GlowHandle | null; ion: TailHandle | null; dust: TailHandle | null;
  jets: Array<{ tail: TailHandle; dir: THREE.Vector3 }>;
  activity: number; distM: number; frame: CometFrame | null; frameAt: number;
}
/** The tail frame is re-read this often (s): a comet turns its tails over minutes, and the read allocates. */
const FRAME_EVERY_S = 0.5;

export function buildComets(env: NaturalEnv, makeup: SystemMakeup, key: string, kit: RockKit): CometSet {
  const root = env.root();
  const comets: Comet[] = [];
  const owned: THREE.Object3D[] = [];
  const release: Array<() => void> = [];
  const tick: Array<(nowS: number) => void> = [];
  const counts = { centaurs: 0, visitors: 0, pebbles: 0 };
  if (!root) return { count: 0, frame: () => { /* torn down */ }, state: () => ({ comets: [] }), dispose: () => { /* nothing */ } };
  const heat = STAR_HEAT[makeup.primary.kind] ?? 1;
  const sun = new THREE.Vector3(), pv = new THREE.Vector3(), q = new THREE.Quaternion(), e = new THREE.Euler();
  const p3: V3 = [0, 0, 0];
  const anchored = (name: string): { group: THREE.Group; abs: THREE.Vector3 } => {
    const group = new THREE.Group();
    group.name = name;
    const abs = new THREE.Vector3();
    root.add(group);
    env.anchor(group, abs);
    release.push(() => env.unanchor(group));
    owned.push(group);
    return { group, abs };
  };
  const sphere = (abs: THREE.Vector3, r: number): ColliderGroup => {
    const g: ColliderGroup = { x: abs.x, y: abs.y, z: abs.z, reach: r * 1.5 + 900, list: [{ x: abs.x, y: abs.y, z: abs.z, r, halfH: 0 }] };
    release.push(env.addCollider(g));
    return g;
  };
  const seatCol = (c: ColliderGroup, abs: THREE.Vector3): void => { c.x = abs.x; c.y = abs.y; c.z = abs.z; c.list[0].x = abs.x; c.list[0].y = abs.y; c.list[0].z = abs.z; };
  const nucleusMat = kit.material('comet-nucleus', COMET_LOOK.nucleus);

  for (const f of makeup.features) {
    if (f.kind === 'comet') {
      const u = (k: string): number => mix01(`${key}:comet:${f.seed}:${k}`);
      const { group, abs } = anchored(`natural-comet:${f.variant}`);
      const nucleus = new THREE.Group();
      // a lumpy dark body — and four in ten are two lobes joined at a neck (67P, Arrokoth)
      const a = kit.body(nucleusMat);
      a.scale.set(f.nucleusM, f.nucleusM * 0.72, f.nucleusM * 0.8);
      nucleus.add(a);
      if (u('bi') < 0.4) {
        const b = kit.body(nucleusMat), r2 = f.nucleusM * (0.5 + u('b2') * 0.15);
        b.scale.set(r2, r2 * 0.85, r2 * 0.9);
        b.rotation.set(u('bx') * 3, u('by') * 3, 0);
        b.position.set(f.nucleusM * 0.95, f.nucleusM * 0.12, 0);
        a.position.set(-r2 * 0.4, 0, 0);
        nucleus.add(b);
      }
      nucleus.rotation.set(u('rx') * 6.283, u('ry') * 6.283, u('rz') * 6.283);
      group.add(nucleus);
      const col = sphere(abs, f.nucleusM * 1.05);
      const live = f.variant !== 'dormant';
      const glow = live ? buildGlow({ color: COMET_LOOK.coma, color2: COMET_LOOK.comaFar, name: 'natural-comet-coma' }) : null;
      const ion = live ? buildTail({ profile: 'ion', color: COMET_LOOK.ion, color2: COMET_LOOK.ionFar, seed: f.seed, segs: 36, across: 6 }) : null;
      const dust = live ? buildTail({ profile: 'dust', color: COMET_LOOK.dust, color2: COMET_LOOK.dustFar, seed: f.seed + 3, g: 0.55, segs: 36, across: 10 }) : null;
      const jets: Comet['jets'] = [];
      if (live && env.level !== 'low') {
        // jets leave the SUNLIT side (the whole lit surface outgasses; a few vents stand out)
        for (let j = 0; j < 3; j++) {
          const tail = buildTail({ profile: 'jet', color: COMET_LOOK.coma, seed: f.seed + 11 + j, g: 0.6, segs: 10, across: 4, bodyR: f.nucleusM * 0.8 });
          jets.push({ tail, dir: new THREE.Vector3(u(`jx${j}`) - 0.5, u(`jy${j}`) - 0.5, u(`jz${j}`) - 0.5).normalize() });
          for (const m of tail.meshes) group.add(m);
        }
      }
      if (glow) group.add(glow.mesh);
      for (const t of [ion, dust]) if (t) for (const m of t.meshes) group.add(m);
      comets.push({ f, group, abs, nucleus, col, glow, ion, dust, jets, activity: 0, distM: 0, frame: null, frameAt: -1e9 });
    } else if (f.kind === 'centaur') {
      // SB-11: a dark body among the giants — neutral grey or very red (the population is bimodal), sometimes in a faint coma of its own
      const look = centaurLook(f.seed);
      const { group, abs } = anchored('natural-centaur');
      const body = kit.body(kit.material('centaur', look));
      body.scale.set(f.radiusM, f.radiusM * 0.82, f.radiusM * 0.9);
      body.rotation.set(mix01(`${key}:cen:${f.seed}:x`) * 6.283, mix01(`${key}:cen:${f.seed}:y`) * 6.283, 0);
      group.add(body);
      const col = sphere(abs, f.radiusM * 0.95);
      const glow = f.active ? buildGlow({ color: 0xcfd6e2, color2: 0x9aa4b6, name: 'natural-centaur-coma' }) : null;
      if (glow) group.add(glow.mesh);
      tick.push((nowS) => {
        orbitAt(f.orbit, nowS, p3);
        abs.set(p3[0], p3[1], p3[2]);
        seatCol(col, abs);
        body.rotation.y = (nowS * 0.004) % (Math.PI * 2);
        if (glow) {
          env.sunAt(abs, sun);
          glow.sun.copy(sun);
          // its activity comes and goes on its own clock (outbursts are not tied to perihelion): a slow breath, never a flash
          const breath = 0.55 + 0.45 * Math.sin((nowS / 1_400) * Math.PI * 2 + f.seed);
          glow.set(f.radiusM * 9, 0.14 * breath, f.radiusM * 1.6);
        }
      });
      counts.centaurs++;
    } else if (f.kind === 'interstellar') {
      // SB-13: elongated, reddish, tumbling, inert — it crosses the system once on a straight line
      const { group, abs } = anchored('natural-visitor');
      const body = kit.body(kit.material('visitor', VISITOR_LOOK));
      const L = f.lengthM / 2;
      group.add(body);
      const col = sphere(abs, L * 0.4);
      const axis = new THREE.Vector3(mix01(`${key}:iv:${f.seed}:x`) - 0.5, mix01(`${key}:iv:${f.seed}:y`) - 0.5, mix01(`${key}:iv:${f.seed}:z`) - 0.5).normalize();
      const from = f.from as V3, to = f.to as V3;
      tick.push((nowS) => {
        visitorAt({ from, to, periodS: f.periodS, phase0: f.phase0 }, nowS, p3);
        abs.set(p3[0], p3[1], p3[2]);
        const x = nowS / f.periodS + f.phase0, uu = x - Math.floor(x);
        // it arrives out of the dark and leaves into it: at the ends of its pass it is not there (the next crossing is the next visitor)
        const k = Math.min(1, uu / 0.02, (1 - uu) / 0.02);
        const s = Math.max(1e-3, k);
        body.scale.set(L * s, L * 0.17 * s, L * 0.13 * s);
        body.quaternion.setFromAxisAngle(axis, (nowS * VISITOR_LOOK.tumble) % (Math.PI * 2)).multiply(q.setFromEuler(e.set(0, 0, 0.6)));
        seatCol(col, abs);
        col.list[0].r = k > 0.5 ? L * 0.4 : 0;
      });
      counts.visitors++;
    } else if (f.kind === 'meteoroid_stream') {
      // SB-14: dark fragments along the comet's own path — no glowing trail in vacuum; they are seen when you are among them
      const peb = streamPebbles(`${key}:ms:${f.seed}`, f.count);
      const im = kit.instanced(kit.material('meteoroids', COMET_LOOK.nucleus), peb.map((p) => ({ at: [0, 0, 0] as V3, sizeM: p.sizeM })), `${key}:msi:${f.seed}`);
      if (!im) continue;
      im.name = 'natural-meteoroid-stream';
      im.frustumCulled = false; // its instances move along the orbit; the bound is not worth refitting for sixty pebbles
      const g = new THREE.Group();
      g.name = 'natural-meteoroids';
      g.add(im);
      root.add(g);
      owned.push(g);
      const base: THREE.Matrix4[] = peb.map((_, i) => { const m = new THREE.Matrix4(); im.getMatrixAt(i, m); return m; });
      const m4 = new THREE.Matrix4(), c0: V3 = [0, 0, 0];
      let seatedAt = -1e9;
      const orbit: OrbitMakeup = f.orbit;
      tick.push((nowS) => {
        // the group rides the comet's own place (a root child at abs − origin, re-seated every frame); the pebbles'
        // offsets from it change with the orbit's curve, slowly — refit every 20 s
        orbitAt(orbit, nowS, c0);
        const o = env.origin();
        g.position.set(c0[0] - o.x, c0[1] - o.y, c0[2] - o.z);
        if (nowS - seatedAt < 20 && nowS >= seatedAt) return;
        seatedAt = nowS;
        const M = meanAnomalyAt(orbit, nowS);
        for (const [i, p] of peb.entries()) {
          orbitPoint(orbit, M + p.dM, p3);
          m4.copy(base[i]).setPosition(p3[0] - c0[0] + p.side[0], p3[1] - c0[1] + p.side[1], p3[2] - c0[2] + p.side[2]);
          im.setMatrixAt(i, m4);
        }
        im.instanceMatrix.needsUpdate = true;
      });
      counts.pebbles += peb.length;
    }
  }

  const updateComet = (c: Comet, nowS: number): void => {
    const f = c.f;
    if (f.orbit) { orbitAt(f.orbit, nowS, p3); c.abs.set(p3[0], p3[1], p3[2]); } else if (f.fixed) c.abs.set(f.fixed[0], f.fixed[1], f.fixed[2]);
    seatCol(c.col, c.abs);
    c.nucleus.rotation.y = (nowS * 0.006) % (Math.PI * 2);
    if (!c.glow || !c.ion || !c.dust) return;
    const dist = env.sunAt(c.abs, sun);
    c.distM = dist;
    if (!c.frame || nowS - c.frameAt >= FRAME_EVERY_S || nowS < c.frameAt) {
      c.frameAt = nowS;
      c.frame = dist === Infinity || !f.orbit
        ? cometFrameFixed([sun.x, sun.y, sun.z], f.seed)
        : cometFrame([c.abs.x, c.abs.y, c.abs.z], orbitVelocity(f.orbit, nowS));
      c.activity = cometActivity(dist, heat, f.variant);
    }
    const fr = c.frame, act = c.activity, sz = cometSizes(act, f.nucleusM);
    c.glow.sun.copy(sun);
    c.glow.set(sz.comaM, COMET_LOOK.comaGain * Math.sqrt(act), f.nucleusM * 2.2);
    // THE TAILS POINT AWAY FROM THE STAR
    for (const t of [c.ion, c.dust]) {
      t.axis.set(fr.anti[0], fr.anti[1], fr.anti[2]);
      t.sun.copy(sun);
      if (fr.cross > 0) t.lag.set(fr.lag[0], fr.lag[1], fr.lag[2]); else t.lag.copy(anyPerp(t.axis, pv));
    }
    c.ion.set(f.nucleusM * 0.6, sz.ionM, 0, sz.ionWidthM * 0.4, sz.ionWidthM * 1.7, COMET_LOOK.ionGain * act);
    c.dust.set(f.nucleusM * 0.6, sz.dustM, fr.cross, sz.dustWidthM * 0.22, sz.dustWidthM, COMET_LOOK.dustGain * act);
    for (const j of c.jets) {
      // a vent turns with the nucleus; it blows only while it faces the star
      pv.copy(j.dir).applyQuaternion(c.nucleus.quaternion);
      const lit = Math.max(0, pv.dot(sun));
      j.tail.axis.copy(pv);
      j.tail.sun.copy(sun);
      j.tail.set(f.nucleusM * 0.75, f.nucleusM * 7, 0, f.nucleusM * 0.12, f.nucleusM * 1.6, COMET_LOOK.jetGain * act * lit);
    }
  };

  return {
    count: comets.length,
    frame: (nowS) => { for (const c of comets) updateComet(c, nowS); for (const t of tick) t(nowS); },
    state: () => ({
      ...counts,
      comets: comets.map((c) => ({
        variant: c.f.variant, abs: [Math.round(c.abs.x), Math.round(c.abs.y), Math.round(c.abs.z)], distM: c.distM === Infinity ? -1 : Math.round(c.distM), activity: +c.activity.toFixed(3),
        anti: c.frame ? c.frame.anti.map((v) => +v.toFixed(7)) : null, lag: c.frame ? c.frame.lag.map((v) => +v.toFixed(7)) : null, cross: c.frame ? +c.frame.cross.toFixed(3) : 0,
        ion: c.ion?.state() ?? null, dust: c.dust?.state() ?? null, lobes: c.nucleus.children.length, jets: c.jets.length,
      })),
    }),
    dispose: () => {
      for (const r of release) { try { r(); } catch { /* released */ } }
      for (const o of owned) deferDisposeTree(o);
    },
  };
}
